// Renders a card body as markdown with `[[c-X]]` mentions turned into
// clickable links that route through `onJumpToCard`. Broken refs (ids not
// in `knownIds`) stay as plain text — the broken-refs row below already
// flags them in red and clicking nowhere is just frustrating.
//
// Implementation: pre-process the body to wrap each known wikilink in a
// markdown link with a sentinel scheme (`#card-ref:c-X`), then route
// clicks on those links through the `components.a` override. The
// rendered text stays `[[c-X]]` so the wikilink syntax is preserved
// visually.
//
// Perf: this component lives in chat bubbles whose parent re-renders
// every streaming token. We `React.memo` the export, hoist `remarkPlugins`
// to module scope, and route `onJumpToCard` through a ref so the
// `components` object only depends on `inline`. Combined with the
// streaming-time plain-text path in MobileChat, history bubbles avoid
// re-parsing markdown on every token.

import { memo, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import * as perf from '../../utils/switch_perf';

// Per-switch counter so we can see "this switch parsed N markdown bodies".
let mdParseCount = 0;
let mdParseTotalMs = 0;
let mdParseFlushScheduled = false;
function noteMdParse(ms: number) {
  mdParseCount += 1;
  mdParseTotalMs += ms;
  if (!mdParseFlushScheduled) {
    mdParseFlushScheduled = true;
    queueMicrotask(() => {
      perf.mark('md-parse/batch', { n: mdParseCount, totalMs: Math.round(mdParseTotalMs * 10) / 10 });
      mdParseCount = 0;
      mdParseTotalMs = 0;
      mdParseFlushScheduled = false;
    });
  }
}

const WIKILINK_RE = /\[\[([a-z][a-z0-9_-]+)\]\]/g;
const SENTINEL_RE = /^#card-ref:(.+)$/;
const REMARK_PLUGINS = [remarkGfm];
const LINK_STYLE: React.CSSProperties = {
  color: 'var(--blue)',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
  textDecorationColor: 'var(--blue)',
  textDecorationThickness: 1,
  cursor: 'pointer',
};

function preprocess(body: string, knownIds: ReadonlySet<string>): string {
  return body.replace(WIKILINK_RE, (raw, id: string) => {
    if (!knownIds.has(id)) return raw;
    // Escape brackets so the rendered link text is literally `[[c-X]]`.
    return `[\\[\\[${id}\\]\\]](#card-ref:${id})`;
  });
}

export interface CardBodyMarkdownProps {
  body: string;
  knownIds: ReadonlySet<string>;
  /** Fires when the user clicks a `[[c-X]]` wikilink. `anchor` is the
   *  clicked link element's bounding rect — consumers can use it to
   *  position a peek popover (CardPeek's computePosition centers when the
   *  anchor doesn't fit). Pre-existing callers using `(id) => void` keep
   *  working — JS ignores the extra arg. */
  onJumpToCard?: (id: string, anchor?: DOMRect) => void;
  /** Strip the wrapping `<p>` so the rendered content sits inline with
   *  surrounding flow content. Use for single-line previews (e.g. a gap
   *  proposal title) where the default paragraph margin would break the
   *  row's visual rhythm. Default false — keep paragraph formatting for
   *  card bodies and chat bubbles. */
  inline?: boolean;
}

function CardBodyMarkdownInner({ body, knownIds, onJumpToCard, inline }: CardBodyMarkdownProps) {
  const { t } = useTranslation();
  // Route clicks through a ref so the memoized `components` object below
  // stays stable when the parent passes a fresh `onJumpToCard` reference.
  // (Most callers do useCallback, but defending against churn here is
  // cheaper than auditing every call site.)
  const jumpRef = useRef(onJumpToCard);
  jumpRef.current = onJumpToCard;
  // `t` likewise routed through a ref so the `components` memo keeps its
  // `[inline]`-only dependency contract (re-render on language change still
  // happens because the whole component re-renders).
  const tRef = useRef(t);
  tRef.current = t;

  const components = useMemo(() => {
    const a = ({ href, children, ...rest }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { children?: React.ReactNode }) => {
      const m = typeof href === 'string' ? href.match(SENTINEL_RE) : null;
      const jump = jumpRef.current;
      if (m && jump) {
        const targetId = m[1];
        return (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              jump(targetId, rect);
            }}
            title={tRef.current('cardBody.jumpTo', { id: targetId })}
            style={LINK_STYLE}
          >
            {children}
          </a>
        );
      }
      return <a href={href} {...rest}>{children}</a>;
    };
    if (inline) {
      return { a, p: ({ children }: { children?: React.ReactNode }) => <>{children}</> };
    }
    return { a };
  }, [inline]);

  const processed = useMemo(() => {
    const t = performance.now();
    const r = preprocess(body, knownIds);
    noteMdParse(performance.now() - t);
    return r;
  }, [body, knownIds]);

  return (
    <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={components}>
      {processed}
    </ReactMarkdown>
  );
}

export const CardBodyMarkdown = memo(CardBodyMarkdownInner);
